import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import useCurrency from '@/hooks/useCurrency';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import { ApplicationTypes } from '@/models/ApplicationType';
import { Currency } from '@/models/Currency';
import {
  PartnerSubCategoryApiResponse,
  ProgramApiResponse,
  ProgramNames,
  ProgramProps,
} from '@/models/Program';

import { ConfigContext } from './ConfigContext';
import { LocaleContext } from './LocaleContext';
import { SelectedPartnerContext } from './SelectedPartnerContext';

interface Props {
  selectedProgramId: ProgramNames;
  selectedProgramInfo: ProgramProps | null;
  programPastAppCycle: {
    [key: number]: ApplicationCycle | null;
  } | null;
  programLatestAppCycle: {
    [key: number]: ApplicationCycle | null;
  } | null;
  programNextAppCycle: {
    [key: number]: ApplicationCycle | null;
  } | null;
  portalOpen: { [key: number]: boolean };
  currencies: Currency[];
  updateSelectedProgramId: (newProgramId: ProgramNames) => void;
}

interface ProviderProps {
  children: React.ReactNode;
}

interface CycleWindow {
  past: ApplicationCycle | null;
  latest: ApplicationCycle | null;
  next: ApplicationCycle | null;
}

export const SelectedProgramContext = createContext<Props>({
  selectedProgramId: ProgramNames._,
  selectedProgramInfo: null,
  programPastAppCycle: null,
  programLatestAppCycle: null,
  programNextAppCycle: null,
  portalOpen: {},
  currencies: [],
  updateSelectedProgramId: () => {},
});

export const SelectedProgramProvider = ({ children }: ProviderProps) => {
  const { allPrograms } = useContext(ConfigContext);
  const { partnerInfo } = useContext(SelectedPartnerContext);
  const { getCurrencies } = useCurrency();
  const { localeLanguageCode, setOverrideLocale, clearOverrideLocale } = useContext(LocaleContext);

  const [selectedProgramId, setSelectedProgramId] = useState<ProgramNames>(ProgramNames._);

  const [selectedProgramInfo, setSelectedProgramInfo] = useState<ProgramProps | null>(null);

  const [programPastAppCycle, setProgramPastAppCycle] = useState<{
    [key: number]: ApplicationCycle | null;
  } | null>(null);

  const [programLatestAppCycle, setProgramLatestAppCycle] = useState<{
    [key: number]: ApplicationCycle | null;
  } | null>(null);

  const [programNextAppCycle, setProgramNextAppCycle] = useState<{
    [key: number]: ApplicationCycle | null;
  } | null>(null);

  const [portalOpen, setPortalOpen] = useState<{
    [key: number]: boolean;
  }>({});

  const [currencies, setCurrencies] = useState<Currency[]>([]);

  useEffect(() => {
    if (selectedProgramId === ProgramNames._) return;
    // Override locale to English if program is not TreeCitiesWorld
    if (selectedProgramId !== ProgramNames.TreeCitiesWorld && selectedProgramId != null) {
      setOverrideLocale('enUS');
    } else if (selectedProgramId === ProgramNames.TreeCitiesWorld) {
      clearOverrideLocale();
    }
  }, [selectedProgramId, setOverrideLocale, clearOverrideLocale]);

  useEffect(() => {
    if (allPrograms != null && selectedProgramId != null) {
      const selectedInfo = allPrograms.find(
        (element: ProgramApiResponse) => element.id == selectedProgramId
      );

      if (
        selectedInfo != null &&
        selectedInfo.partnerSubCategories != null &&
        localeLanguageCode != null
      ) {
        setSelectedProgramInfo({
          ...selectedInfo,
          partnerTerm:
            selectedInfo.partnerTerm[localeLanguageCode != null ? localeLanguageCode : 'en'],
          partnerSubCategories: selectedInfo.partnerSubCategories.map(
            (subCategory: PartnerSubCategoryApiResponse) =>
              // skip if subCategory is null
              subCategory?.subCategory != null
                ? {
                    ...subCategory,
                    subCategory:
                      subCategory.subCategory[
                        localeLanguageCode != null ? localeLanguageCode : 'en'
                      ],
                  }
                : null
          ),
        } as ProgramProps);
      }
    }
  }, [allPrograms, selectedProgramId, localeLanguageCode]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      const response = await getCurrencies();

      setCurrencies(response);
    };

    if (currencies?.length > 0 || selectedProgramId != ProgramNames.TreeCitiesWorld) return;

    void fetchCurrencies();
  }, [currencies?.length, getCurrencies, selectedProgramId]);

  const updateSelectedProgramId = useCallback((newProgramId: ProgramNames) => {
    setSelectedProgramId(newProgramId);
    if (newProgramId == null) {
      setSelectedProgramInfo(null);
    }
  }, []);

  const getCycleWindow = useCallback(
    (applicationTypeId: ApplicationTypes, stateId: string | null = null): CycleWindow => {
      if (selectedProgramInfo == null) {
        return { past: null, latest: null, next: null };
      }

      const currentDate = new Date();
      const matchingCycles = [...(selectedProgramInfo.applicationCycles ?? [])]
        .filter(
          (cycle: ApplicationCycle) =>
            cycle.applicationTypeId === applicationTypeId &&
            (stateId != null ? cycle.stateId === stateId : true)
        )
        .sort(
          (a: ApplicationCycle, b: ApplicationCycle) =>
            new Date(a.openDate).valueOf() - new Date(b.openDate).valueOf()
        );

      let latest: ApplicationCycle | null = null;
      let next: ApplicationCycle | null = null;

      for (const cycle of matchingCycles) {
        if (new Date(cycle.openDate) <= currentDate) {
          latest = cycle;
        } else if (next == null) {
          next = cycle;
        }
      }

      let past: ApplicationCycle | null = null;
      if (latest != null) {
        const pastYear = latest.year - 1;
        for (const cycle of matchingCycles) {
          if (cycle.year === pastYear) {
            past = cycle;
          }
        }
      }

      return { past, latest, next };
    },
    [selectedProgramInfo]
  );

  useEffect(() => {
    const mainApplicationTypeCycleWindow = getCycleWindow(ApplicationTypes.Standard);

    const nextAppCycle: { [key: number]: ApplicationCycle | null } = {
      [ApplicationTypes.Standard]: mainApplicationTypeCycleWindow.next,
    };

    if (selectedProgramId === ProgramNames.TreeCityUSA && partnerInfo?.stateId != null) {
      const stateQuestionCycleWindow = getCycleWindow(
        ApplicationTypes.StateQuestions,
        partnerInfo.stateId
      );

      const growthAwardCycleWindow = getCycleWindow(ApplicationTypes.GrowthAward);

      nextAppCycle[ApplicationTypes.StateQuestions] = stateQuestionCycleWindow.next;
      nextAppCycle[ApplicationTypes.GrowthAward] = growthAwardCycleWindow.next;
    }

    setProgramNextAppCycle(nextAppCycle);
  }, [getCycleWindow, partnerInfo?.stateId, selectedProgramId]);

  useEffect(() => {
    const currentDate = new Date();

    const mainApplicationTypeCycleWindow = getCycleWindow(ApplicationTypes.Standard);

    const mainApplicationPortalOpen =
      mainApplicationTypeCycleWindow.latest != null
        ? new Date(mainApplicationTypeCycleWindow.latest.closeDate) > currentDate
        : false;

    const portalOpenDictionary: {
      [key: number]: boolean;
    } = {
      [ApplicationTypes.Standard]: mainApplicationPortalOpen,
    };

    const pastAppCycle: {
      [key: number]: ApplicationCycle | null;
    } = {
      [ApplicationTypes.Standard]: mainApplicationTypeCycleWindow.past,
    };

    const latestAppCycle: {
      [key: number]: ApplicationCycle | null;
    } = {
      [ApplicationTypes.Standard]: mainApplicationTypeCycleWindow.latest,
    };

    const growthAwardCycleWindow = getCycleWindow(ApplicationTypes.GrowthAward);

    let stateQuestionCycleWindow = null;
    if (partnerInfo?.stateId != null) {
      stateQuestionCycleWindow = getCycleWindow(
        ApplicationTypes.StateQuestions,
        partnerInfo.stateId
      );
    }

    const stateQuestionOpen =
      stateQuestionCycleWindow?.latest != null
        ? new Date(stateQuestionCycleWindow.latest.closeDate) > currentDate
        : false;

    const growthAwardOpen =
      growthAwardCycleWindow.latest != null
        ? new Date(growthAwardCycleWindow.latest.closeDate) > currentDate
        : false;

    if (stateQuestionCycleWindow != null)
      pastAppCycle[ApplicationTypes.StateQuestions] = stateQuestionCycleWindow?.past;

    pastAppCycle[ApplicationTypes.GrowthAward] = growthAwardCycleWindow.past;

    if (stateQuestionCycleWindow?.latest != null)
      latestAppCycle[ApplicationTypes.StateQuestions] = stateQuestionCycleWindow?.latest;

    latestAppCycle[ApplicationTypes.GrowthAward] = growthAwardCycleWindow.latest;
    portalOpenDictionary[ApplicationTypes.StateQuestions] = stateQuestionOpen;
    portalOpenDictionary[ApplicationTypes.GrowthAward] = growthAwardOpen;

    setPortalOpen(portalOpenDictionary);
    setProgramPastAppCycle(pastAppCycle);
    setProgramLatestAppCycle(latestAppCycle);
  }, [getCycleWindow, partnerInfo?.stateId, selectedProgramId]);

  return (
    <SelectedProgramContext.Provider
      value={{
        selectedProgramId,
        selectedProgramInfo,
        programPastAppCycle,
        programLatestAppCycle,
        programNextAppCycle,
        portalOpen,
        currencies,
        updateSelectedProgramId,
      }}
    >
      {children}
    </SelectedProgramContext.Provider>
  );
};
